import React, { useState } from 'react'
import classnames from 'classnames'
import { ChevronDownIcon } from '@heroicons/react/24/solid'

export interface PanelProps extends Omit<React.ComponentProps<'div'>, 'header'> {
  header: React.ReactElement | string
  isHighlighted?: boolean
  /** When true, header toggles body visibility. */
  collapsible?: boolean
  /** Initial collapsed state when `collapsible` is true. Default: false (open). */
  defaultCollapsed?: boolean
}

export const Panel: React.FC<PanelProps> = (props) => {
  const { className, isHighlighted, header, children, collapsible = false, defaultCollapsed = false, ...rest } = props
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const open = !collapsed

  const headerClassName = classnames(
    'py-2 px-4 font-semibold',
    isHighlighted ? 'bg-orange-50 dark:bg-orange-400/30' : 'bg-indigo-100 dark:bg-slate-700',
    open ? 'rounded-t-lg' : 'rounded-lg'
  )

  return (
    <div
      className={classnames('w-full bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700', className)}
      {...rest}
    >
      {collapsible ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setCollapsed((v) => !v)}
          className={classnames(
            headerClassName,
            'flex w-full items-center justify-between gap-2 text-left hover:brightness-[0.98] dark:hover:brightness-110'
          )}
        >
          <span>{header}</span>
          <ChevronDownIcon
            className={classnames('h-4 w-4 shrink-0 text-slate-600 transition-transform dark:text-slate-300', open && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
      ) : (
        <div className={classnames(headerClassName, 'rounded-t-lg')}>{header}</div>
      )}
      {(!collapsible || open) && <div className="py-3 px-4 rounded-b-lg">{children}</div>}
    </div>
  )
}

export default Panel
